import _, { isArray, isDate, isObject } from 'lodash';
import { FieldError } from 'react-hook-form';
import { AxiosError } from 'axios';
import { getCountries, getPhoneCode } from 'libphonenumber-js';
import { Guid } from 'guid-typescript';
import { getStoreRef } from './storeRef';

// Alias for backward compatibility with getStore() calls throughout this file
const getStore = getStoreRef;

import {
  BaseResponse,
  KeyValue,
  KeyValueDefault,
  ResultError
} from '../models/common';
import { CultureEnum } from './enum/CultureEnum';
import { MenuItemResponse, MenuResponse } from '../models/response/menu';
import { ControlResponse } from '../models/response/controlResponse';
import { HttpStatusEnum } from './enum/HttpStatusEnum';
import { formatNumberWithPadding } from './utilsNumber';
import {
  LASTSYNCTRANSLATIONS,
  STORAGE_TOKEN,
  TRANSLATIONS,
  getStorage,
  getStringStorage
} from './storage';
import {
  RoutesConstants,
  TypeRoutesConstants
} from '../routes/routesConstants';
import { hasValueString, uppercaseFirstLetter } from './utilsString';
import { publicAddressIpApi } from '../api';
import { firstElementList } from './utilsArray';
import { DefaultValueParameterEnum } from './enum/DefaultValueParameterEnum';
import { CustomError } from './class/customError';
import { ErrorGeneralEnum } from './enum/ErrorEnum';
import { toast } from 'react-toastify';
import { CompanyTypeEnum } from './enum/CompanyTypeEnum';
import { KeyValueId } from '../models/common/keyValueId';
import { ApplicationState } from '@/main/store/rootStore';
import { useSelector } from 'react-redux';

export function removeNulls(obj: any): any {
  if (obj === null) {
    return undefined;
  }
  if (typeof obj === 'object') {
    for (const key in obj) {
      obj[key] = removeNulls(obj[key]);
    }
  }
  return obj;
}

export function getKeyOrder(obj: Array<KeyValue>): Array<any> {
  let arrayOrdered: any = [];
  if (obj) {
    arrayOrdered = obj.sort((a, b) =>
      a.key > b.key ? 1 : b.key > a.key ? -1 : 0
    );
  }

  return arrayOrdered;
}

export function isLeapYear(year: number): boolean {
  return year % 4 == 0 && (year % 100 != 0 || year % 400 == 0);
}

export function scrollIntoViewDown(element: HTMLInputElement) {
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });
  }
}

export function formartUri(uri: string) {
  const prefixHttps = 'https://';
  const prefixHttp = 'http://';
  if (uri) {
    uri = uri.trim();

    if (!uri.startsWith(prefixHttps) && !uri.startsWith(prefixHttp)) {
      uri = `${prefixHttp}${uri}`;
    }

    return uri;
  }

  return '';
}

export function debounce(func: Function, wait: number) {
  let timeout: number | null;
  return function (...args: any) {
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func.apply(context, args);
    }, wait);
  };
}

export function removeEmptyValues<T extends object>(obj: T): T {
  const cloneObj = _.cloneDeep(obj);
  return Object.entries(cloneObj).reduce((acc, [key, value]) => {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 'undefined' ||
      value === 'null' ||
      (isArray(value) && value.length === 0) ||
      (isObject(value) && !isDate(value) && Object.keys(value).length === 0)
    ) {
      return acc;
    }

    return {
      ...acc,
      [key]: value
    };
  }, {} as T);
}

export function getDefaultOptionDrop<T>(
  data: T[] | null,
  keySelected: string | null = null
): T | null {
  if (data !== null && data.length > 0) {
    if (keySelected) {
      return data.find(x => x.key === keySelected) || null;
    }
    if (data.length === 1) return data[0];
  }
  return null;
}

export function getDefaultCountryOptionDrop(
  data: KeyValueDefault[] | null
): KeyValueDefault | null {
  if (!data || data.length === 0) return null;
  if (data.length === 1) return data[0];

  const findCountryDefault = data.find(item => item.default === 'True');
  return findCountryDefault || data[0];
}

export function haveIgnoreElement(
  element: HTMLElement | null
): HTMLElement | null {
  while (element) {
    const containsIgnore = element.classList.contains('ignore-click'); // IMPROVE: use data attribute to not impact in DOM
    if (containsIgnore) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
}

export function getCultureBrowser() {
  const MAX_LENGTH_CULTURE_CHARACTER = 5;
  if (navigator && navigator.languages && navigator.languages.length > 0) {
    const culturesBrowserFiltered = navigator.languages.filter(
      x => x.length === MAX_LENGTH_CULTURE_CHARACTER
    );
    if (culturesBrowserFiltered && culturesBrowserFiltered.length > 0) {
      // Get first language (with Country) defined in browser of User
      return culturesBrowserFiltered[0];
    }
  }

  return CultureEnum.esMX;
}

export function getTranslateByKey(
  key: string,
  isError: boolean = false,
  translatesLocal: KeyValue[] = []
) {
  const KEY_NAME_DEFAULT_APP = 'nah';
  const translatesStorage = getStore().getState().translate.translates.data;
  const translates: KeyValue[] | null =
    translatesLocal.length > 0 ? translatesLocal : translatesStorage;
  if (translates !== null && translates.length > 0) {
    let translateValue: string = 'Translate not found';
    const keyFormatted = `${
      isError ? 'e' : 't'
    }.${KEY_NAME_DEFAULT_APP}.${key}`;

    const translateFound = translates.find(
      (x: KeyValue) =>
        String(x.key).toLocaleLowerCase() === keyFormatted.toLocaleLowerCase()
    );

    if (translateFound && translateFound.value) {
      translateValue = translateFound.value;
    }

    return translateValue;
  }

  return 'Translates is empty';
}

export function getTranslateByFullKey(
  key: string,
  translatesLocal: KeyValue[] = []
) {
  const translatesStorage = getStore().getState().translate.translates.data;
  const translates: KeyValue[] | null =
    translatesLocal.length > 0 ? translatesLocal : translatesStorage;
  if (translates !== null && translates.length > 0) {
    let translateValue: string = 'Translate not found';

    const translateFound = translates.find(
      (x: KeyValue) =>
        String(x.key).toLocaleLowerCase() === key.toLocaleLowerCase()
    );

    if (translateFound && translateFound.value) {
      translateValue = translateFound.value;
    }

    return translateValue;
  }

  return 'Translates is empty';
}
export function getParamsToGetTranslates() {
  // Get idCultureUser
  const idCultureUser: string | null = getCultureBrowser();

  // Get lastSyncTranslations
  let lastSyncTranslations: string | null = null;
  const lastSyncTranslationsStoraged = getStringStorage(
    `${LASTSYNCTRANSLATIONS}_${idCultureUser}`
  );

  if (hasValueString(lastSyncTranslationsStoraged)) {
    lastSyncTranslations = lastSyncTranslationsStoraged;
  }

  return { lastSyncTranslations, idCultureUser };
}

export function getParamsToGetTranslatesPublic(idCultureUser?: string) {
  // Get idCultureUser
  idCultureUser ? idCultureUser : getCultureBrowser();

  // Get lastSyncTranslations
  let lastSyncTranslations: string | null = null;
  const lastSyncTranslationsStoraged = getStringStorage(
    `${LASTSYNCTRANSLATIONS}_${idCultureUser}`
  );

  if (hasValueString(lastSyncTranslationsStoraged)) {
    lastSyncTranslations = lastSyncTranslationsStoraged;
  }

  return { lastSyncTranslations, idCultureUser };
}

export function composeTranslates(
  idCultureUserLogged: string,
  dataTranslateApi: KeyValue[]
) {
  // Get translates local storage
  const keyTranslateStorage = `${TRANSLATIONS}_${idCultureUserLogged}`;
  const translatationsStoraged: KeyValue[] | null = getStorage(
    keyTranslateStorage
  );
  let translatesUpdated: KeyValue[] = [];

  if (translatationsStoraged && translatationsStoraged.length > 0) {
    translatesUpdated = translatationsStoraged;
    for (let index = 0; index < dataTranslateApi.length; index++) {
      const translateApi = dataTranslateApi[index];
      const translateFound = translatationsStoraged.find(
        ({ key }) => key === translateApi.key
      );

      if (translateFound) {
        translatesUpdated = translatesUpdated.map((item: KeyValue) => {
          return item.key === translateApi.key
            ? { ...item, value: translateApi.value }
            : item;
        });
      } else {
        translatesUpdated.push(translateApi);
      }
    }
  }

  return translatesUpdated;
}

export function getScreensWithoutModule() {
  const menu: MenuResponse[] | null = getStore().getState().menu.menu.data;
  const screens: MenuItemResponse[] = [];
  if (menu !== null && menu.length > 0) {
    const modulesWithScreens: MenuResponse[] = menu.filter(
      (x: MenuResponse) => x.screens && x.screens.length > 0
    );
    if (modulesWithScreens.length > 0) {
      modulesWithScreens.forEach((menu: MenuResponse) => {
        menu.screens.forEach((menuItem: MenuItemResponse) => {
          screens.push(menuItem);
        });
      });
    }
  }

  return screens;
}

export function hasAccessByRoute(route: string) {
	const screens = getScreensWithoutModule();
	return screens.some((screen) => screen.route == route);
}

export function getGuidScreenByRoute(route: RoutesConstants) {
  const screens = getScreensWithoutModule();

  if (screens.length > 0) {
    const screen: MenuItemResponse | undefined = screens.find(
      (x: MenuItemResponse) => getRouteWithoutParams(x.route) === route
    );
    if (screen) {
      return screen.guid;
    }
  }
  return Guid.EMPTY;
}

export function isShowControlByElementId(elementId: string) {
  let isShow: boolean = false;
  //  Notation: This variable "controlsByScreen" is already filterd by Screen
  const controlsByScreen: ControlResponse[] | null = getStore().getState().control
    .controlsByScreen.data;
  if (controlsByScreen !== null && controlsByScreen.length > 0) {
    const elementControl: ControlResponse | undefined = controlsByScreen.find(
      (x: ControlResponse) => x.elementId === elementId
    );
    if (elementControl) {
      isShow = true;
    }
  }

  return isShow;
}

export function useIsShowControlByElementId(elementId: string): boolean {
  const controlsByScreen = useSelector(
    (state: ApplicationState) => state.control.controlsByScreen.data
  );

  if (!controlsByScreen || controlsByScreen.length === 0) return false;

  return controlsByScreen.some(
    (x: ControlResponse) => x.elementId === elementId
  );
}

export function getRouteWithoutParams(route: string) {
  const indexBaseUrl = 0;
  const quantitySplitByParams = 2;
  const routeSplitted = route.split('?');

  if (routeSplitted && routeSplitted.length > 0) {
    if (routeSplitted.length === quantitySplitByParams) {
      const routerWithoutParams = routeSplitted[indexBaseUrl];
      return routerWithoutParams;
    }
  }

  return route;
}

export function isShowElementActionForCreateRegisterByRoute(route: string) {
  let isShow: boolean = false;
  const screens = getScreensWithoutModule();

  if (screens.length > 0) {
    const screen: MenuItemResponse | undefined = screens.find(
      (x: MenuItemResponse) => getRouteWithoutParams(x.route) === route
    );
    if (screen) {
      isShow = true;
    }
  }

  return isShow;
}

export function getNameElementSelected(e: React.ChangeEvent<HTMLInputElement>) {
  if (e.target.tagName) {
    return e.target.tagName;
  }

  if (e.target.nodeName) {
    return e.target.nodeName;
  }

  return '';
}

export function getDefaultMessageResultsFound(
  dataLength: number,
  totalItems: number
) {
  const title = getTranslateByKey('information.resultsFound');
  return title
    .replace('{0}', formatNumberWithPadding(dataLength))
    .replace('{1}', formatNumberWithPadding(totalItems));
}

export function isNullUndefined<T>(
  value: T | null | undefined
): value is null | undefined {
  return value === null || value === undefined;
}

export function isNullUndefinedEmpty<T>(
  value: T | null | undefined
): value is null | undefined {
  return value === null || value === undefined || value === '';
}

export function isObjectEmpty(object: {}): boolean {
  return Object.keys(object).length === 0;
}

interface IResultErrorReduxSaga {
  messageError: string;
  messagesDataError: string[];
}

export function errorBoundaryReduxSaga(
  error: AxiosError<ResultError | BaseResponse>
): IResultErrorReduxSaga {
  const messageCodeError = error.message;

  if (error.response?.status === HttpStatusEnum.InternalServerError) {
    const dataInternalError = error.response.data as any;
    return {
      messageError: dataInternalError.Message,
      messagesDataError: [messageCodeError]
    };
  }

  if (error.response && error.response.data) {
    const isResultError = error.response.data.hasOwnProperty('errors');

    if (isResultError) {
      const responseError = error.response.data as ResultError;
      const messageTitleError = responseError.title;
      const mappedErrors = Object.keys(responseError.errors).map(key => ({
        field: key,
        message: responseError.errors[key] as string[]
      }));
      const messagesDataError = mappedErrors.map(
        item => `${item.field}: ${item.message.map(itemError => itemError)}`
      );
      const messageError = `${messageCodeError}\n   ${messageTitleError}\n `;
      return {
        messageError,
        messagesDataError
      };
    }
  }

  return {
    messageError: '',
    messagesDataError: []
  };
}

export function renderErrorReduxSaga(error: IResultErrorReduxSaga) {
  const { messageError, messagesDataError } = error;
  if (messagesDataError.length === 0 || messageError === '') return;

  console.error(`Error: ${messageError} - ${messagesDataError.join('\n ')}`);
}

export function openNewTab(screen: string) {
  window.open(`#${screen}`, '_blank');
}
export function openNewTabExternalLink(url: string) {
  window.open(url, '_blank');
}

/**
 * Function to render error messages
 *
 * @param {string }label - Label of field
 * @param {FieldError | undefined} error  - Error of field
 * @example
 * const { register, errors } = useForm();
 *
 * <input type="text" name="name" ref={register({ required: true })} />
 * {renderErrorMessages('Name', errors.name)} // Name is required
 *
 * @returns {string | null} Return error message
 *
 */
export function renderErrorMessages(
  label: string | null = null,
  error: FieldError | undefined
) {
  const requiredMessage = getTranslateByKey('information.isRequired', true);
  const fieldMessage = getTranslateByKey('information.field');

  if (!error) return null;
  switch (error.type) {
    case 'required':
      return uppercaseFirstLetter(
        requiredMessage.replace('{0}', `${String(label || fieldMessage)}`)
      );
    default:
      return error.message;
  }
}

export function clearCache() {
  window.caches.keys().then(cacheNames => {
    cacheNames.forEach(cacheName => {
      window.caches
        .open(cacheName)
        .then(cache => {
          return cache.keys();
        })
        .then(requests => {
          requests.forEach(() => {
            window.caches.delete(cacheName);
          });
        });
    });
  });
}

export const isDiffZero = (value: number) =>
  value !== 0 && !isNullUndefined(value);

export function getMaskIpAddress() {
  return /^([1-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(\.([0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3}$/;
}

export function validIPAddress(ipAddress: string): boolean {
  const regex = new RegExp(getMaskIpAddress());
  return regex.test(ipAddress);
}

export async function getIpUser(maxTries = 0) {
  try {
    if (maxTries > 6) return null;

    if (maxTries > 3) {
      const { data } = await publicAddressIpApi.getPublicAddressGeolocationDb();
      return data.IPv4;
    }
    const { data } = await publicAddressIpApi.getPublicAddressIpify();
    return data.ip;
  } catch (error) {
    return getIpUser(maxTries + 1);
  }
}

export function getFirstParameterType() {
  const parameterRedux: KeyValue[] | null = getStore().getState().parameterByType
    .parameterByType.data;
  if (parameterRedux && parameterRedux.length > 0) {
    const parameter = firstElementList(parameterRedux);
    if (parameter) {
      return parameter;
    }
  }
}
export function getRangeParameterType(
  parameterType: KeyValue | null | undefined
): number {
  const defaultValue = DefaultValueParameterEnum.FallbackRangeDate;
  if (isNullUndefined(parameterType)) return defaultValue;

  const parameterValue = Number(parameterType.value);
  return isNullUndefined(parameterValue) ? defaultValue : parameterValue;
}

export function buildUrlQueryParams(
  route: RoutesConstants,
  params: { [key: string]: string | undefined | null }
) {
  // validte if params is empty
  const urlParams = Object.entries(params)
    .map(([key, value]) =>
      isNullUndefined(value)
        ? false
        : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .filter(Boolean)
    .join('&');

  return `${route}?${urlParams}`;
}

export function mountReducerWithData(data: KeyValue[] | null) {
  return {
    data,
    isLoading: false,
    isError: false,
    errorMessages: []
  };
}
interface IKeyValueParams extends KeyValue {
  isQueryParams?: boolean;
}
export function isValueQueryParams(data: IKeyValueParams | undefined | null) {
  if (!isNullUndefined(data)) {
    const isQueryParams = data.isQueryParams || data.value === '';
    return isQueryParams;
  }
  return false;
}

export function isStringISODate(value: string) {
  const regex = new RegExp(
    '^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d{3})?([+-]\\d{2}:\\d{2}|Z)$'
  );
  return regex.test(value);
}
interface IDefaultReducer {
  data: KeyValue[] | null;
  isLoading: boolean;
  isError: boolean;
  errorMessages: string[] | null;
}
type IDefaultReducerSuccess = IDefaultReducer & {
  data: KeyValue[];
  isLoading: false;
  isError: false;
  errorMessages: null;
};
export function haveValueInDomain(
  reducer: IDefaultReducer
): reducer is IDefaultReducerSuccess {
  if (
    !isNullUndefined(reducer.data) &&
    !reducer.isLoading &&
    reducer.data.length > 0
  ) {
    return true;
  }
  return false;
}

export function isEmpty<T>(
  value: T | null | undefined
): value is null | undefined {
  if (isNullUndefined(value)) return true;

  switch (typeof value) {
    case 'object':
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return Object.keys(value as object).length === 0;
    case 'string':
      return (
        value === '' ||
        value === ' ' ||
        value === 'null' ||
        value === 'undefined'
      );
    case 'number':
      return value === 0;
    case 'boolean':
      return value === false;
    default:
      return false;
  }
}

export function isKeyTranslate(key: string): boolean {
  return key.startsWith('t.') || key.startsWith('e.');
}

export type RemoveNull<T> = {
  [K in keyof T]: NonNullable<T[K]>;
};

export function hexToRgba(hex: string, alpha: number): string | null {
  // Check if the input is a valid hex color code
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    return null;
  }

  // Normalize the hex color string
  let normalizedHex = hex.replace('#', '');

  // Convert shorthand hex color to full hex color
  if (normalizedHex.length === 3) {
    normalizedHex = normalizedHex
      .split('')
      .map(c => c + c)
      .join('');
  }

  // Extract RGB values
  const r = parseInt(normalizedHex.substring(0, 2), 16);
  const g = parseInt(normalizedHex.substring(2, 4), 16);
  const b = parseInt(normalizedHex.substring(4, 6), 16);

  // Ensure alpha is within the valid range [0, 1]
  const clampedAlpha = Math.min(Math.max(alpha, 0), 1);

  // Return the RGBA string
  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
}
export function removeObjectsEmpty<T>(obj: any, applyNull = false): T {
  const newObj = { ...obj };
  Object.keys(newObj).forEach(key => {
    if (isEmpty(newObj[key])) {
      if (applyNull) {
        newObj[key] = null;
      } else {
        delete newObj[key];
      }
    }
  });
  return newObj;
}

// Reducer utilities moved to '@/main/services/reducerUtils' to break circular dependency
export {
  INITIAL_REDUCER_DATA,
  haskeyReducer,
  updateGetIsolated,
  updateSaveIsolated,
  resetIsolatedReducer,
  isFinishedReducer
} from './reducerUtils';
export type {
  IInitialReducerData,
  IReducerWithIsolated
} from './reducerUtils';

export function getKeyRouteConstantByRoute(route: string): TypeRoutesConstants {
  return Object.keys(RoutesConstants).find(
    key => RoutesConstants[key as TypeRoutesConstants] === route
  ) as TypeRoutesConstants;
}
interface IUseResponsiveResponse {
  totalWidthPercent: number;
  totalWidthPX: number;
}

export function useResponsiveTable(
  sizeColumns: string[] | undefined
): IUseResponsiveResponse {
  if (isNullUndefined(sizeColumns))
    return { totalWidthPercent: 0, totalWidthPX: 0 };
  const sizeColumnsWithoutResponsive = sizeColumns?.filter(
    size => size !== 'responsive'
  );
  const isPercentage = (size: string) => size.includes('%');
  const totalWidthPercent = sizeColumnsWithoutResponsive.reduce(
    (acc, size) =>
      isPercentage(size) ? acc + Number(size.replace('%', '')) : acc,
    0
  );
  const totalWidthPX = sizeColumnsWithoutResponsive.reduce(
    (acc, size) =>
      !isPercentage(size) ? acc + Number(size.replace('px', '')) : acc,
    0
  );

  return { totalWidthPercent, totalWidthPX };
}

export function isGuidEmpty(guid: string) {
  if (isNullUndefined(guid)) return true;
  return guid === Guid.EMPTY;
}

export function getPhoneCountryCode() {
  const countries = getCountries();
  const codeCountries = countries.map(item => {
    const phoneCode = getPhoneCode(item);
    return {
      key: phoneCode,
      value: `+${phoneCode}`
    };
  });

  const uniqueCodeCountries = codeCountries.filter(
    (item, index, self) => index === self.findIndex(t => t.key === item.key)
  );

  uniqueCodeCountries.sort((a, b) => a.key - b.key);

  return uniqueCodeCountries;
}

export function GetPhoneCode(phoneNumber: string | undefined) {
  if (isNullUndefined(phoneNumber)) return { codePhone: '', number: '' };
  const phoneSplit = phoneNumber.split(' ');
  if (phoneSplit.length === 1) return { codePhone: '', number: phoneSplit[0] };
  return {
    codePhone: phoneSplit[0],
    number: phoneSplit[1]
  };
}

export function getAPIPublicAccessKey() {
  return import.meta.env.VITE_API_PUBLIC_ACCESSKEY;
}

export function getToken() {
  return getStringStorage(STORAGE_TOKEN);
}

export function getAPIAuthorization() {
  return `Bearer ${getToken()}`;
}

export function isPublicAPI(url: string) {
  return url.includes('_public') || url.includes('Cache');
}

export function getGoogleRecaptchaPublicKey(): string {
  return import.meta.env.VITE_GOOGLE_RECAPTCHA_PUBLIC_KEY ?? '';
}

export function getStripePublishableKey(): string {
  return import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';
}

export function handleGeneralError() {
  const MESSAGE_ERROR_DEFAULT = getTranslateByKey(
    'information.anUnexpectedErrorOccurredPleaseTryAgainLater',
    true
  );
  toast.error(MESSAGE_ERROR_DEFAULT);
  throw new Error(MESSAGE_ERROR_DEFAULT);
}

export const hextoRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Function convert string to decimal percentage
 *
 * @param {string }value - value in string
 * @example
 * const exampleValue = '50';
 *
 * const result = convertStringToDecimalPercentage(exampleValue) // 50
 * console.log(result) //0.5
 *
 * @returns {number} Return error message
 *
 */
export const convertStringToDecimalPercentage = (value: string) => {
  if (isEmpty(value)) return 0;

  const valueConverted = value.replace(',', '.');
  const floatValue = parseFloat(valueConverted);
  return floatValue / 100;
};

/**
 * Function convert string to decimal
 *
 * @param {string }value - value in string
 * @example
 * const exampleValue = '50,45';
 *
 * const result = convertStringToDecimal(exampleValue) // 50,45
 * console.log(result) //50.45
 *
 * @returns {number} Return error message
 *
 */
export const convertStringToDecimal = (value: string) => {
  if (isEmpty(value)) return 0;

  const valueConverted = value.replace(',', '.');
  const floatValue = parseFloat(valueConverted);
  return floatValue;
};

export type IConditionMessage = {
  condition: boolean;
  message: string;
};

export function filterCompanyTypeByEnums(
  data: KeyValue[] | null,
  enums: CompanyTypeEnum[]
) {
  if (isNullUndefined(data)) return null;

  return data.filter(item => enums.includes(Number(item.key)));
}
export const convertDecimalToString = (value: number) => {
  if (isEmpty(value)) return '0';

  const valueConverted = (value * 100).toFixed(2);

  if (valueConverted.endsWith('00')) {
    return valueConverted.slice(0, -3);
  }

  return valueConverted;
};

export function orderDataByValue(data: KeyValue[]) {
  return data.sort((a, b) => a.value.localeCompare(b.value));
}
export function isValidGuid(guid: string | undefined): guid is string {
  return !!guid && guid !== Guid.EMPTY;
}

export function isKeyValueDefault(
  option: KeyValue | KeyValueDefault
): option is KeyValueDefault {
  return typeof option === 'object' && 'default' in option;
}

export function createKeyValue(key: string | number, value: string): KeyValue {
  return {
    key,
    value
  };
}
export function createKeyValueId(
  key: string | number,
  value: string,
  id: string | number
): KeyValueId {
  return {
    key,
    value,
    id
  };
}

export function createOptionalKeyValue(
  guid: string | undefined,
  value: string
): KeyValue | null {
  return isValidGuid(guid) ? createKeyValue(guid, value) : null;
}

export function createOptionalKeyValueId(
  guid: string | undefined,
  name: string,
  id: string
): KeyValueId | null {
  return isValidGuid(guid) ? createKeyValueId(guid, name, id) : null;
}
export function removeParamsFromURL(url: string, keyParams: string) {
  const [path, queryString] = url.split('?');

  if (!queryString) {
    return url;
  }

  const params = new URLSearchParams(queryString);

  params.delete(keyParams);

  const updatedQueryString = params.toString();
  return updatedQueryString ? `${path}?${updatedQueryString}` : path;
}

export const getKeyFromKeyValue = (data: KeyValue) =>
  isNullUndefined(data) ? '' : data.key;

export const enumToKeyValue = (
  enumObj: any,
  translateGroup: string
): KeyValue[] => {
  return Object.keys(enumObj)
    .filter((key) => isNaN(Number(key)))
    .map((item) => ({
      key: enumObj[item],
      value: getTranslateByKey(`${translateGroup}.${item}`)
    }));
};

/**
 *
 * @param url
 * @returns sub domain
 * @example
 * const url = www.subdomain.domain.com.br;
 * const subDomain = getSubDomainFromUrl(url);
 * console.log(subDomain); // subdomain
 */
export const getSubDomainFromUrl = (url: string) => {
  try {
    const urlObj = new URL(url);
    const hostParts = urlObj.hostname.split('.');
    if (hostParts.length > 1) {
      return hostParts[0];
    }
    return null;
  } catch (error) {
    return null;
  }
};
/**
 *
 * @returns boolean if is subdomain qualitas
 * @example
 * const isQualitas = isSubDomainQualitas();
 * console.log(isQualitas); // true or false
 */
export const isSubDomainQualitas = (): boolean => {
  const url = window.location.href;
  const subDomain = getSubDomainFromUrl(url);
  return subDomain === 'qualitas';
};

